import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import type { SensorAlertBundle, SensorView } from '@mark43/cad-api';
import {
    Box,
    Button,
    calculatePageCount,
    DataTablePagination,
    EmptyState,
    Flex,
    Grid,
    Text,
    VStack,
} from '@cad-arc';
import { Panel } from '../panels/panel';
import { RESIZE_DISABLED } from '../panels/panel-configuration';
import { WorkspacePanel } from '../panels/workspace-panel';
import { CameraVideo } from './camera-video';
import { SensorAlertRow } from './sensor-alert-row';
import { SensorAlertThumbnail } from './sensor-alert-thumbnail';
import {
    findNearestSensors,
    formatSensorAlertTime,
    formatSensorCoordinates,
    formatSensorDistance,
    formatSensorPlace,
    isMappableSensor,
    NearbySensor,
} from './helpers';
import { useOpenSensorPanel } from './use-open-sensor-panel';
import { useSensorDispatchAreaName } from './use-sensor-dispatch-area-name';
import { useSensorsStore } from './use-sensors-store';
import { VideoModeBadge } from './video-player-chrome';

/**
 * Prefixed with the panel's own type, so a stack of sensor and sensor event panels over the map is
 * readable without reading their contents. The place itself comes from `formatSensorPlace`, shared
 * with the alert toast so the two name a camera the same way.
 */
function getSensorPanelTitle(sensor: SensorView): string {
    return ['Sensor', formatSensorPlace(sensor)].filter(Boolean).join(' · ');
}

function SensorPanelHeader({
    sensor,
    sensorNumber,
    sensorCount,
}: {
    sensor: SensorView;
    sensorNumber: number;
    sensorCount: number;
}): React.ReactElement {
    return (
        /*
         * No padding here: `PanelHeader`'s own HStack already applies `padding={1}`, and adding a
         * second inset pushed this whole block 4px below the close button it sits beside.
         */
        <Flex flexGrow={1} minWidth={0} alignItems="flex-start" gap={2}>
            <VStack align="stretch" spacing={0} minWidth={0}>
                <Text variant="headingXs" noOfLines={1}>
                    {getSensorPanelTitle(sensor)}
                </Text>
                <Text variant="caption" color="text.secondary" noOfLines={1}>
                    {[sensor.name, `Sensor ${sensorNumber} of ${sensorCount}`]
                        .filter(Boolean)
                        .join(' · ')}
                </Text>
            </VStack>
        </Flex>
    );
}

/**
 * Label above value. The label is the design's small mono eyebrow; the value stays mono and bold,
 * as it was asked for before the design arrived.
 */
function MetadataItem({
    label,
    children,
    spansRow = false,
}: {
    label: string;
    children: React.ReactNode;
    /** Take the whole grid row, for the address. */
    spansRow?: boolean;
}): React.ReactElement {
    return (
        <Box gridColumn={spansRow ? '1 / -1' : undefined} minWidth={0}>
            <VStack align="stretch" spacing={1}>
                <Text
                    variant="caption"
                    color="tertiary"
                    fontFamily="mono"
                    fontSize="10px"
                    casing="uppercase"
                    style={{ letterSpacing: '0.12em' }}
                >
                    {label}
                </Text>
                {children}
            </VStack>
        </Box>
    );
}

function MetadataValue({ children }: { children: React.ReactNode }): React.ReactElement {
    return (
        <Text variant="bodyMd" fontFamily="mono" fontWeight="bold" noOfLines={1}>
            {children}
        </Text>
    );
}

const SensorMetadata = observer(function SensorMetadata({
    sensor,
}: {
    sensor: SensorView;
}): React.ReactElement {
    // The address, or where the camera is when the vendor gave it no address.
    const place =
        sensor.address ?? formatSensorCoordinates(sensor.latitude, sensor.longitude) ?? '–';
    const dispatchAreaName = useSensorDispatchAreaName(sensor);

    return (
        <Grid
            templateColumns="repeat(3, minmax(0, 1fr))"
            gap={4}
            padding={4}
            // Divides it from the player the way each section below is divided from the last.
            borderTop="sm"
            borderColor="border.default"
        >
            <MetadataItem label="Site">
                <MetadataValue>{sensor.site ?? '–'}</MetadataValue>
            </MetadataItem>
            <MetadataItem label="Last online">
                <MetadataValue>
                    {sensor.online
                        ? 'Now'
                        : sensor.lastOnlineDateUtc
                        ? formatSensorAlertTime(sensor.lastOnlineDateUtc)
                        : '–'}
                </MetadataValue>
            </MetadataItem>
            <MetadataItem label="D-Area">
                <MetadataValue>{dispatchAreaName ?? '–'}</MetadataValue>
            </MetadataItem>
            <MetadataItem label="Address" spansRow>
                <MetadataValue>{place}</MetadataValue>
            </MetadataItem>
        </Grid>
    );
});

/**
 * A busy camera can accumulate up to `MAX_SENSOR_ALERTS` events in a shift, which is far more than
 * belongs in a panel. Paging is client-side because the whole log is already in the store.
 */
const SENSOR_EVENTS_PAGE_SIZE = 5;

/**
 * The page on screen. Page one is live: arrivals prepend to it. A later page is pinned to the
 * alert that was first on it when it was opened, so an arrival does not shift its rows out from
 * under the reader — only the total in the range text moves, as the design asks.
 */
type EventsPage = { index: number; anchorAlertKey?: string };

function getPageStart(alerts: SensorAlertBundle[], page: EventsPage): number {
    if (page.index > 0 && page.anchorAlertKey) {
        const anchorIndex = alerts.findIndex(
            (alert) => alert.alertKey === page.anchorAlertKey
        );
        if (anchorIndex >= 0) {
            return anchorIndex;
        }
    }
    return page.index * SENSOR_EVENTS_PAGE_SIZE;
}

const SensorRecentEvents = observer(function SensorRecentEvents({
    sensorId,
}: {
    sensorId: string | undefined;
}): React.ReactElement {
    const sensorsStore = useSensorsStore();
    const alerts = sensorsStore.getAlertsForSensor(sensorId);
    const [requestedPage, setRequestedPage] = useState<EventsPage>({ index: 0 });

    const pageCount = calculatePageCount(alerts.length, SENSOR_EVENTS_PAGE_SIZE);
    /*
     * Clamped on read rather than corrected in an effect. The log is live and capped, so the page
     * count can shrink under a reader who is sitting on the last page; deriving it means that
     * resolves during the same render instead of flashing an empty page first.
     */
    const page =
        requestedPage.index > pageCount - 1 ? { index: Math.max(pageCount - 1, 0) } : requestedPage;
    const pageStart = getPageStart(alerts, page);
    const alertsOnPage = alerts.slice(pageStart, pageStart + SENSOR_EVENTS_PAGE_SIZE);

    function goToPage(index: number, start: number) {
        setRequestedPage(
            index === 0
                ? { index: 0 }
                : { index, anchorAlertKey: alerts[start] && alerts[start].alertKey }
        );
    }

    return (
        <VStack
            align="stretch"
            spacing={2}
            paddingX={4}
            // Less on top: the header row is pager height, which already centres the title
            // lower. This keeps it as far below the rule as the other section titles sit.
            paddingTop={2}
            paddingBottom={4}
            borderTop="sm"
            borderColor="border.default"
        >
            {/* The pager sits in the header rather than under the list, so it stays put when the
                last page is short. */}
            <Flex alignItems="center" gap={2} minHeight="32px">
                <Text variant="headingXs">Recent sensor events</Text>
                {/* Surfaced rather than swallowed, as in the drawer: a list that silently stops
                    short reads as "that is all there was". */}
                {sensorsStore.isAlertHistoryTruncated ? (
                    <Text variant="caption" color="text.secondary">
                        Older events are not shown
                    </Text>
                ) : undefined}
                <Box marginLeft="auto">
                    {pageCount > 1 ? (
                        /* No border: this is Arc's table footer, used here in a section header
                           where the rule it draws above itself has nothing to separate. */
                        <DataTablePagination
                            hasBorder={false}
                            size="sm"
                            pageIndex={page.index}
                            pageSize={SENSOR_EVENTS_PAGE_SIZE}
                            totalSize={alerts.length}
                            onPrevPage={() =>
                                goToPage(
                                    page.index - 1,
                                    Math.max(pageStart - SENSOR_EVENTS_PAGE_SIZE, 0)
                                )
                            }
                            onNextPage={() =>
                                goToPage(page.index + 1, pageStart + SENSOR_EVENTS_PAGE_SIZE)
                            }
                        />
                    ) : undefined}
                </Box>
            </Flex>
            {alerts.length === 0 ? (
                <Text variant="caption" color="text.secondary">
                    No sensor events received for this camera yet.
                </Text>
            ) : (
                /* One-pixel gaps over the border colour, so rows are divided without each
                   carrying a border of its own. */
                <VStack
                    align="stretch"
                    spacing="1px"
                    borderRadius="md"
                    overflow="hidden"
                    backgroundColor="border.default"
                    data-testid="SENSOR_PANEL_EVENT_LIST"
                >
                    {alertsOnPage.map((alert) => (
                        <SensorAlertRow key={alert.alertKey} alert={alert} />
                    ))}
                </VStack>
            )}
        </VStack>
    );
});

/** Poster bitmap for a nearby tile: 16:9, and small — see `SensorAlertThumbnail`. */
const NEARBY_POSTER_WIDTH = 240;
const NEARBY_POSTER_HEIGHT = 135;
const NEARBY_SENSOR_COUNT = 3;

const NearbySensorTile = observer(function NearbySensorTile({
    sensor,
}: {
    sensor: NearbySensor;
}): React.ReactElement {
    const sensorsStore = useSensorsStore();
    const openSensorPanel = useOpenSensorPanel();
    const latestAlert = sensorsStore.getLatestAlertForSensor(sensor.externalId);
    const openThisSensor = () => openSensorPanel({ sensorId: sensor.externalId });

    return (
        <VStack align="stretch" spacing={2} minWidth={0} data-testid="SENSOR_PANEL_NEARBY_SENSOR">
            {/*
              * A still, not a stream: three concurrent live feeds under the main one would be the
              * expensive thing the design rules out. The still is the camera's latest event
              * capture, drawn once to a canvas rather than decoded as an animated gif.
              */}
            <Box
                as="button"
                type="button"
                position="relative"
                width="100%"
                padding={0}
                borderRadius="md"
                overflow="hidden"
                border="sm"
                borderColor="border.default"
                backgroundColor="surface.background"
                cursor="pointer"
                aria-label={`Open ${formatSensorPlace(sensor) ?? 'sensor'}`}
                onClick={openThisSensor}
                style={{ aspectRatio: '16 / 9' }}
            >
                {latestAlert?.imageUrl ? (
                    <SensorAlertThumbnail
                        imageUrl={latestAlert.imageUrl}
                        width={NEARBY_POSTER_WIDTH}
                        height={NEARBY_POSTER_HEIGHT}
                        fill
                    />
                ) : undefined}
                <Box position="absolute" top={1} left={1} pointerEvents="none">
                    <VideoModeBadge mode={sensor.online ? 'live' : 'signalLost'} />
                </Box>
            </Box>
            <Text variant="bodyMd" fontWeight="bold" noOfLines={1}>
                {formatSensorPlace(sensor) ?? sensor.name ?? 'Unknown sensor'}
            </Text>
            <Flex alignItems="center" gap={2}>
                <Box flex="1" minWidth={0}>
                    <Text variant="caption" color="text.secondary" fontFamily="mono" noOfLines={1}>
                        {`${formatSensorDistance(sensor.distanceMeters)} ${sensor.bearing}`}
                    </Text>
                </Box>
                <Button size="sm" trailingVisual="OpenNewWindow" onClick={openThisSensor}>
                    Open
                </Button>
            </Flex>
        </VStack>
    );
});

/**
 * The three cameras nearest this one, nearest first. Fewer than three leaves the remaining
 * columns empty rather than stretching what there is.
 *
 * Not the grid panel's fill-to-nine demo hack: this lists only cameras that exist, once each.
 */
const SensorPanelNearbySensors = observer(function SensorPanelNearbySensors({
    sensor,
}: {
    sensor: SensorView;
}): React.ReactElement | null {
    const sensorsStore = useSensorsStore();
    if (!isMappableSensor(sensor)) {
        return null;
    }
    const nearbySensors = findNearestSensors(
        sensorsStore.sensors.filter((candidate) => candidate.externalId !== sensor.externalId),
        { latitude: sensor.latitude, longitude: sensor.longitude },
        NEARBY_SENSOR_COUNT
    );
    if (nearbySensors.length === 0) {
        return null;
    }

    return (
        <VStack align="stretch" spacing={2} padding={4} borderTop="sm" borderColor="border.default">
            <Flex alignItems="baseline" gap={2}>
                <Text variant="headingXs">Nearby sensors</Text>
                <Text variant="caption" color="text.secondary">
                    {nearbySensors.length === 1 ? 'Closest' : `${nearbySensors.length} closest`}
                </Text>
            </Flex>
            <Grid templateColumns={`repeat(${NEARBY_SENSOR_COUNT}, minmax(0, 1fr))`} gap={3}>
                {nearbySensors.map((nearbySensor) => (
                    <NearbySensorTile key={nearbySensor.externalId} sensor={nearbySensor} />
                ))}
            </Grid>
        </VStack>
    );
});

const SensorPanelBody = observer(function SensorPanelBody({
    sensorId,
}: {
    sensorId: string | undefined;
}): React.ReactElement {
    const sensorsStore = useSensorsStore();
    const sensor = sensorsStore.getSensor(sensorId);
    const latestAlert = sensorsStore.getLatestAlertForSensor(sensorId);

    if (!sensor) {
        return (
            <VStack align="stretch" padding={2} data-testid="SENSOR_PANEL_BODY">
                <EmptyState
                    visual="Camera"
                    title="Sensor unavailable"
                    subtitle="This sensor is no longer in the sensor list for your department."
                />
            </VStack>
        );
    }

    return (
        <VStack
            align="stretch"
            spacing={0}
            backgroundColor="surface.canvas"
            data-testid="SENSOR_PANEL_BODY"
        >
            {/* Full screen is the player's own control, so there is no separate action for it. */}
            <Box flexShrink={0}>
                <CameraVideo
                    sensorId={sensorId}
                    fallbackImageUrl={latestAlert?.imageUrl}
                    alerts={sensorsStore.getAlertsForSensor(sensorId)}
                />
            </Box>
            <SensorMetadata sensor={sensor} />
            <SensorRecentEvents key={sensorId} sensorId={sensorId} />
            <SensorPanelNearbySensors sensor={sensor} />
        </VStack>
    );
});

const SensorPanelHeaderContainer = observer(function SensorPanelHeaderContainer({
    sensorId,
}: {
    sensorId: string | undefined;
}): React.ReactElement {
    const sensorsStore = useSensorsStore();
    const sensor = sensorsStore.getSensor(sensorId);
    const sensors = sensorsStore.sensors;
    const sensorNumber = sensors.findIndex((candidate) => candidate.externalId === sensorId) + 1;

    if (!sensor) {
        return (
            <Flex padding={1}>
                <Text variant="headingXs">Sensor</Text>
            </Flex>
        );
    }

    return (
        <SensorPanelHeader
            sensor={sensor}
            sensorNumber={sensorNumber}
            sensorCount={sensors.length}
        />
    );
});

export function SensorPanel({ panel }: { panel: WorkspacePanel<'SENSOR'> }): React.ReactElement {
    const sensorId = panel.getPanelState('sensorId');

    return (
        // As tall as its content, at a fixed width, so nothing in it scrolls.
        <Panel
            panel={panel}
            testId="SENSOR_PANEL"
            resizeOptions={RESIZE_DISABLED}
            heightFromContent
            header={<SensorPanelHeaderContainer sensorId={sensorId} />}
            body={<SensorPanelBody sensorId={sensorId} />}
        />
    );
}
